import { Referral, ReferralStatus } from 'src/constants';

// Pending is a workspace that signed up before its signup grant landed, so it
// counts as a signup alongside the new statuses.
const SIGNED_UP_STATUSES: ReadonlySet<ReferralStatus> = new Set([
  ReferralStatus.In_Trial,
  ReferralStatus.Signed_Up,
  ReferralStatus.Converted,
]);

export type ReferralSummary = {
  signedUp: number;
  converted: number;
  creditsEarned: number;
};

// A growth-loops referral accumulates the credits each grant paid in `amount`,
// so it holds what actually landed: a referral denied at first paid still
// shows its signup credits, and one whose grant was skipped shows none. This
// page only lists growth-loops referrals, never the old cash payouts.
export const getReferralCredits = (referral: Referral): number =>
  referral.amount;

export const hasEarnedCredits = (referral: Referral): boolean =>
  getReferralCredits(referral) > 0;

// A referral denied at signup never earns anything, but one denied at first
// paid keeps the signup credits it already got — so it did sign up.
const isSignup = (referral: Referral): boolean =>
  SIGNED_UP_STATUSES.has(referral.status) ||
  (referral.status === ReferralStatus.Denied && hasEarnedCredits(referral));

export const getReferralSummary = (referrals: Referral[]): ReferralSummary =>
  referrals.reduce<ReferralSummary>(
    // Credits count every row so the total matches the history column: a
    // referral denied at first paid still earned its signup credits.
    (summary, referral) => ({
      signedUp: summary.signedUp + (isSignup(referral) ? 1 : 0),
      converted:
        summary.converted +
        (referral.status === ReferralStatus.Converted ? 1 : 0),
      creditsEarned: summary.creditsEarned + getReferralCredits(referral),
    }),
    { signedUp: 0, converted: 0, creditsEarned: 0 },
  );

/** Invited rows have no workspace yet, so the invitee's email stands in. */
export const getReferralCompanyName = (referral: Referral): string =>
  referral.referredWorkspaceName || referral.onboardedUserEmail || referral.id;

// The list endpoint assembles rows concurrently, so its order is arbitrary and
// changes between loads; the id tie-break keeps equal timestamps stable too.
export const sortReferralsNewestFirst = (referrals: Referral[]): Referral[] =>
  [...referrals].sort(
    (a, b) =>
      Date.parse(b.createdAt) - Date.parse(a.createdAt) ||
      a.id.localeCompare(b.id),
  );
